/**
 * Eraya price-test sync for the Hey Hobby theme.
 *
 * Two jobs:
 *
 * 1. VARIANT CHANGE — Hey Hobby's variant-picker fetches a fresh card/section
 *    from the Section Rendering API and morphs it into the DOM, then dispatches
 *    a bubbling `variant:update` custom event on the picker element.  By the
 *    time that event reaches document the DOM is already up-to-date, so we just
 *    call updatePricesForProduct and the SDK rewrites the test price in-place.
 *
 * 2. AJAX CARDS — product-recommendations.js, predictive search, and quick-add
 *    all inject product cards after page load.  We watch for any new
 *    [data-product-id] nodes and debounce a full-page rescan so the SDK can
 *    stamp test prices on the new cards.
 */
(function () {
  'use strict';

  // ── helpers ────────────────────────────────────────────────────────────────

  function getManager() {
    try {
      var E = window.Eraya;
      if (E && E.sdk && typeof E.sdk.getPriceTestManager === 'function') {
        return E.sdk.getPriceTestManager();
      }
    } catch (_) {}
    return null;
  }

  // ── 1. Variant change ──────────────────────────────────────────────────────
  //
  // Hey Hobby dispatches `variant:update` (ThemeEvents.variantUpdate) after the
  // Section Rendering API response has been morphed into the DOM.
  // event.detail = { resource: variantObject, data: { productId, html, … } }
  //
  // On product cards the morph targets the variant-picker itself (not the whole
  // card), so product-price / .price keep their existing DOM nodes — we just
  // overwrite the text content with the test price.

  document.addEventListener('variant:update', function (event) {
    var detail = event.detail;
    if (!detail) return;

    var variantId = detail.resource && String(detail.resource.id);
    var productId = detail.data && String(detail.data.productId);
    if (!variantId || !productId) return;

    // Keep every [data-product-id] root that matches this product in sync so
    // the SDK's container/variant-id checks pass correctly.
    var roots = document.querySelectorAll('[data-product-id="' + productId + '"]');
    roots.forEach(function (root) {
      root.setAttribute('data-variant-id', variantId);
    });

    var mgr = getManager();
    if (!mgr || typeof mgr.updatePricesForProduct !== 'function') return;

    try {
      mgr.updatePricesForProduct(productId, variantId);
    } catch (_) {
      // never break the storefront
    }
  });

  // ── 2. AJAX-injected product cards ─────────────────────────────────────────
  //
  // product-recommendations.js fetches the recommendation section via the
  // Section Rendering API and replaces innerHTML, which means the product cards
  // are not in the DOM when the Eraya SDK initialises.  Same applies to
  // predictive search results and quick-add modal cards.
  //
  // Strategy: watch for any element carrying data-product-id being added to the
  // DOM, debounce to a single call, then ask the SDK to rescan the whole page.

  var rescanTimer = null;

  function rescan() {
    var mgr = getManager();
    if (!mgr) return;
    try {
      if (typeof mgr.updatePricesOnCurrentPage === 'function') {
        mgr.updatePricesOnCurrentPage();
      }
    } catch (_) {}
  }

  function scheduleRescan() {
    clearTimeout(rescanTimer);
    // 150 ms: enough for the theme to finish rendering the cards but short
    // enough that users don't see the un-tested price for long.
    rescanTimer = setTimeout(rescan, 150);
  }

  function nodeHasProductId(node) {
    if (node.nodeType !== 1) return false;
    if (node.hasAttribute && node.hasAttribute('data-product-id')) return true;
    return typeof node.querySelector === 'function' &&
      node.querySelector('[data-product-id]') !== null;
  }

  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (nodeHasProductId(added[j])) {
            scheduleRescan();
            return; // one schedule per batch is enough
          }
        }
      }
    });

    function startObserver() {
      // childList + subtree only — we only care about new cards entering the
      // DOM. We deliberately don't watch attributes: the SDK rewrites price text
      // itself, and watching attributes would risk re-triggering on our own work.
      observer.observe(document.body, { childList: true, subtree: true });
    }

    if (document.body) {
      startObserver();
    } else {
      document.addEventListener('DOMContentLoaded', startObserver);
    }
  }
})();
